<template>
  <div class="container-fluid page-container">
    <div v-if="shouldBeVisible">
      <div class="row page-header__container mb-4 pb-3">
        <page-heading :class="'col align-items-center'" :title="pageTitle" />
      </div>
      <b-row>
        <b-card
          no-body
          class="bg-white border rounded shadow w-100 p-2 p-md-4"
        >
          <h2 class="pb-3 mb-3 border-bottom">
            {{ $t('Win More Work With Our New Customer Finance Feature') }}
          </h2>
          <i18n path="applyForLoanMsg" tag="p">
            <template #applyForLoanVideoLink>
              <a href="#finance-video" class="text-decoration-underline">
                {{ $t('applyForLoanVideoLink') }}
              </a>
            </template>
          </i18n>
          <h3 class="pb-2">
            {{ $t('How Does It Work?') }}
          </h3>
          <p>
            {{ $t('financeIsAvailable') }}
          </p>
          <p>
            {{ $t('financeFeature') }}
          </p>

          <div class="row mx-0 mb-4 justify-content-center align-items-start">
            <b-card class="option border p-3 mr-0 mr-md-3 mb-3 col-12 col-lg-5">
              <b-card-text class="d-flex flex-column align-items-center justify-content-between">
                <div class="text-center">
                  <h4>{{ $t('Option 1') }}</h4>
                  <h5>{{ $t('Offer finance via your dashboard') }}</h5>
                </div>
                <b-btn
                  variant="primary"
                  class="mt-3"
                  :to="{name: 'my-leads'}"
                >
                  {{ $t('Select from My Leads') }}
                </b-btn>
              </b-card-text>
            </b-card>
            <b-card class="option border p-3 ml-0 ml-md-3 col-12 col-lg-5">
              <div class="d-flex flex-column align-items-stretch container">
                <h4 class="text-center">
                  {{ $t('Option 2') }}
                </h4>
                <h5 class="text-center">
                  {{ $t('Offer finance to your own customer') }}
                </h5>
                <div class="row">
                  <div class="col">
                    <b-form-select id="title-input" v-model="title" :disabled="!canOfferFinance" :options="titleOptions" :placeholder="$t('Title')" />
                  </div>
                  <div class="col">
                    <b-form-input v-model="forename" :disabled="!canOfferFinance" :placeholder="$t('First Name')" class="mb-1" />
                  </div>
                </div>
                <div class="row">
                  <div class="col col-12">
                    <b-form-input v-model="lastname" :disabled="!canOfferFinance" :placeholder="$t('Surname')" class="mb-1" />
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <b-form-input v-model="email" :disabled="!canOfferFinance" :placeholder="$t('Email')" class="mb-1" />
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <b-form-select v-model="site" :disabled="!canOfferFinance" :options="siteOptions" :placeholder="$t('Choose a site')" />
                  </div>
                </div>
                <b-btn variant="primary" class="mt-3" :disabled="!canOfferFinance" @click="offerExternal">
                  {{ $t('Send') }}
                </b-btn>
                <p>
                  <small>
                    {{ $t('customerFinanceDetails') }}
                  </small>
                </p>
              </div>
            </b-card>
          </div>

          <h3 class="pb-2">
            {{ $t('Benefits For You') }}
          </h3>
          <ul class="mb-4">
            <li v-html="$t('Beat the Competition')" />
            <li v-html="$t('No Hassle')" />
            <li v-html="$t('No Costs')" />
            <li v-html="$t('Less Discounts')" />
            <li v-html="$t('No Risk or Extra Admin')" />
          </ul>

          <h3 class="pb-2">
            {{ $t('Benefits For Your Customer') }}
          </h3>
          <ul>
            <li>{{ $t('Spreading the cost makes the work affordable.') }}</li>
            <li>{{ $t('Funds can be received within as little as 1 hour.') }}</li>
            <li>{{ $t('Compare up to 24 lenders without impacting credit score.') }}</li>
            <li>{{ $t('Interest rates from as low as 3% with a range of repayment options.') }}</li>
            <li>{{ $t('Good and Bad credit history accepted.') }}</li>
            <li>{{ $t('Reduced risk with an unsecured personal loan.') }}</li>
          </ul>
          <div id="finance-video" class="row mx-0 mb-4 justify-content-center col-12">
            <div class="col-md-6 mt-4 mb-4">
              <div class="video-container">
                <iframe
                  :src="$t('offerFinanceVideo')"
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen
                />
              </div>
            </div>
          </div>
        </b-card>
      </b-row>
    </div>
    <div> Sorry, you cannot access this page. </div>
  </div>
</template>

<script>
import PageHeading from "@/components/control/PageHeading";
import notification from "@/imports/mixins/notification";
import company from "@/imports/mixins/company";
import companyAccount from "@/imports/mixins/companyAccount";
const TITLE = 'Offer Finance';
const BOILERGUIDE_ID = 1;
const SOLARGUIDE_ID = 2;

export default {
  name: 'OfferFinance',
  title: TITLE,
  components: {PageHeading},
  mixins: [notification, company, companyAccount],
  data() {
    return {
      title: null,
      forename: '',
      lastname: '',
      email: '',
      site: null,
    }
  },
  computed: {
    shouldBeVisible(){
      return this.showFinance && this.companyCanOfferFinance && !this.isLimitedAccount;
    },
    siteOptions() {
      return [
        { value: null, text: this.$t('Select Site'), disabled: true },
        { value: BOILERGUIDE_ID, text: 'Boiler Guide'},
        { value: SOLARGUIDE_ID, text: 'Solar Guide'},
      ];
    },
    titleOptions() {
      return [
        { value: null, text: this.$t('Title'), disabled: true },
        { value: 'Mr', text: this.$t('Mr')},
        { value: 'Mrs', text: this.$t('Mrs')},
        { value: 'Ms', text: this.$t('Ms')},
        { value: 'Miss', text: this.$t('Miss')},
        { value: 'Dr', text: this.$t('Dr')},
      ];
    },
    canOfferFinance() {
      return this.$store.state.company.COMPANY.can_offer_finance;
    },
    pageTitle() {
      return this.$t(TITLE);
    }
  },
  methods: {
    async offerExternal() {
      let toastMessage = null;
      let toastType = null;

      try {
        await this.$api.leads.offerFinanceExternal(this.email, this.title, this.forename, this.lastname, this.site)
        toastMessage = this.$t('Finance introduction sent to {title} {lastname}',{title: this.title, lastname: this.lastname});
        toastType = 'success';
      } catch (e) {
        toastMessage = this.$t('Sorry, there was an error. The finance introduction has not been sent. Please refresh the page and try again.');
        toastType = 'error';
      }

      this.notify(
        this.$bvToast,
        toastMessage,
        null,
        toastType
      );
    },
  },
  created() {
  }
}
</script>

<style scoped lang="scss">
  .card,
  .btn {
    font-size: $font-size-14;
  }
  .video-container {
    position:relative;
    padding-bottom:56.25%;
    padding-top:30px;
    height:0;
    overflow:hidden;
    width: 100%;
  }
  .video-container iframe, .video-container object, .video-container embed {
    position:absolute;
    top:0;
    left:0;
    width:100%;
    height:100%;
  }
  .text-decoration-underline {
    text-decoration: underline;
  }
</style>
